import type { FeatureFlags } from '@/lib/api'

/**
 * The features a client sees on "Plan features and limits". A client-facing allowlist on
 * purpose: engineering and internal flags never appear here. Each entry reads the resolved
 * `profile.featureFlags`; a feature that is not switched on for the client is not listed.
 */
export type CustomerFeatureFlag = Extract<
  keyof FeatureFlags,
  | 'createStudy'
  | 'createAudience'
  | 'chatRoomsEnabled'
  | 'marketplaceAccess'
  | 'assistantPlatform'
  | 'socialListening'
  | 'conversationWebSearch'
  | 'agentHandler'
  | 'brandedTheme'
  | 'slackTwins'
>

export interface CustomerFeature {
  flag: CustomerFeatureFlag
  order: number
  /** True when the platform treats a missing value as on (the flag's default is on). */
  onWhenMissing?: boolean
}

export const CUSTOMER_FEATURES: ReadonlyArray<CustomerFeature> = [
  { flag: 'createStudy', order: 10 },
  { flag: 'createAudience', order: 20 },
  { flag: 'chatRoomsEnabled', order: 30, onWhenMissing: true },
  { flag: 'marketplaceAccess', order: 40, onWhenMissing: true },
  { flag: 'assistantPlatform', order: 50 },
  { flag: 'socialListening', order: 60 },
  { flag: 'conversationWebSearch', order: 70 },
  { flag: 'agentHandler', order: 80 },
  { flag: 'brandedTheme', order: 90 },
  { flag: 'slackTwins', order: 100 },
]

/** The listed features switched on for this client, in display order. */
export function enabledCustomerFeatures(
  flags: Partial<FeatureFlags> | null | undefined,
): Array<CustomerFeature> {
  return [...CUSTOMER_FEATURES]
    .sort((a, b) => a.order - b.order)
    .filter((feature) => {
      const value = flags?.[feature.flag]
      return value === undefined ? feature.onWhenMissing === true : value === true
    })
}
